import { Injectable } from '@angular/core';

export type UtmTouch = {
  source: string;
  medium: string;
  campaign: string;
  content: string;
  term: string;
  landingPath: string;
  capturedAt: string;
};

export type UtmAttribution = {
  firstTouch: UtmTouch;
  lastTouch: UtmTouch;
};

const FIRST_KEY = 'mb_utm_first';
const LAST_KEY = 'mb_utm_last';
const COOKIE = 'mb_utm_ft';
const MAX_LEN = 80;
const PII_HINT =
  /@|password|token|secret|bearer\s|eyj[a-z0-9_-]{20,}|[0-9]{10,}|user[_-]?id|phone|ssn|card/i;

const SOURCE_ALIASES: Record<string, string> = {
  google: 'google',
  facebook: 'facebook',
  fb: 'facebook',
  instagram: 'instagram',
  ig: 'instagram',
  linkedin: 'linkedin',
  youtube: 'youtube',
  tiktok: 'tiktok',
  chatgpt: 'chatgpt',
  openai: 'chatgpt',
  email: 'email',
  referral: 'referral',
  twitter: 'twitter',
  x: 'twitter',
  whatsapp: 'whatsapp',
};

const MEDIUM_ALIASES: Record<string, string> = {
  organic: 'organic',
  paid: 'paid_search',
  cpc: 'paid_search',
  ppc: 'paid_search',
  paid_search: 'paid_search',
  paid_social: 'paid_social',
  social: 'social',
  email: 'email',
  referral: 'referral',
  affiliate: 'affiliate',
};

function slugify(raw: unknown, aliases?: Record<string, string>): string {
  const original = String(raw || '').trim();
  if (!original || PII_HINT.test(original)) return '';
  const v = original
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, MAX_LEN);
  if (!v || PII_HINT.test(v)) return '';
  return (aliases && aliases[v]) || v;
}

function isValid(t: UtmTouch | null | undefined): t is UtmTouch {
  return !!t && Boolean(t.source || t.medium || t.campaign);
}

@Injectable({ providedIn: 'root' })
export class UtmService {
  captureUtmParameters(): UtmTouch | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const touch: UtmTouch = {
      source: slugify(params.get('utm_source'), SOURCE_ALIASES),
      medium: slugify(params.get('utm_medium'), MEDIUM_ALIASES),
      campaign: slugify(params.get('utm_campaign')),
      content: slugify(params.get('utm_content')),
      term: slugify(params.get('utm_term')),
      landingPath: (window.location.pathname || '/').slice(0, 200),
      capturedAt: new Date().toISOString(),
    };
    if (!isValid(touch)) return this.getLastTouchUtm();
    this.write(LAST_KEY, touch);
    if (!this.getFirstTouchUtm()) {
      this.write(FIRST_KEY, touch);
      this.writeCookie(touch);
    }
    return touch;
  }

  getFirstTouchUtm(): UtmTouch | null {
    const stored = this.read(FIRST_KEY);
    if (isValid(stored)) return stored;
    const cookie = this.readCookie();
    if (isValid(cookie)) {
      this.write(FIRST_KEY, cookie);
      return cookie;
    }
    return null;
  }

  getLastTouchUtm(): UtmTouch | null {
    const stored = this.read(LAST_KEY);
    return isValid(stored) ? stored : this.getFirstTouchUtm();
  }

  getUtmAttribution(): UtmAttribution | null {
    const firstTouch = this.getFirstTouchUtm();
    const lastTouch = this.getLastTouchUtm();
    if (!firstTouch && !lastTouch) return null;
    return {
      firstTouch: firstTouch || lastTouch!,
      lastTouch: lastTouch || firstTouch!,
    };
  }

  clearUtmParameters(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(FIRST_KEY);
    localStorage.removeItem(LAST_KEY);
    if (typeof document !== 'undefined') {
      document.cookie = `${COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
    }
  }

  appendUtmParameters(url: string, touch?: Partial<UtmTouch> | null): string {
    const src = touch || this.getFirstTouchUtm();
    if (!url || !src) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      const map: Record<string, string> = {
        utm_source: src.source || '',
        utm_medium: src.medium || '',
        utm_campaign: src.campaign || '',
        utm_content: src.content || '',
        utm_term: src.term || '',
      };
      for (const [k, v] of Object.entries(map)) {
        if (!v || parsed.searchParams.has(k)) continue;
        parsed.searchParams.set(k, v);
      }
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private read(key: string): UtmTouch | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as UtmTouch) : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: UtmTouch): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }

  private readCookie(): UtmTouch | null {
    if (typeof document === 'undefined') return null;
    const row = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE}=`));
    if (!row) return null;
    try {
      return JSON.parse(decodeURIComponent(row.slice(COOKIE.length + 1))) as UtmTouch;
    } catch {
      return null;
    }
  }

  private writeCookie(touch: UtmTouch): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(touch))}; Max-Age=${90 * 24 * 60 * 60}; Path=/; SameSite=Lax`;
  }
}
